import { type FC, type ReactNode, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { FormControl, FormHelperText, FormLabel, Option, Select } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { type ContactFormValues } from '../../model/ContactFormValues'
import { directContactFormAccessorGetSubjects } from '../../model/directContactFormAccessorGetSubjects'

export const DirectContactFormSubjectField: FC<{
  disabled?: boolean
  includePrefilledOption?: boolean
  isSubjectPharmaRelated?: boolean
  onSubjectChange: () => void
  renderOptionValue?: (value: string) => ReactNode
  selectRootAriaLabel?: string
}> = ({
  disabled,
  includePrefilledOption,
  isSubjectPharmaRelated,
  onSubjectChange,
  renderOptionValue,
  selectRootAriaLabel,
}) => {
  const intl = useIntl()
  const [isListboxOpen, setIsListboxOpen] = useState(false)
  const { tenant } = useGlobalConfigContext()
  const { control } = useFormContext<ContactFormValues>()
  const { options } = directContactFormAccessorGetSubjects({
    intl,
    isSubjectPharmaRelated,
    tenant,
  })
  const label = intl.formatMessage({ id: 'general.label.subject' })
  const requiredMessage = intl.formatMessage({ id: 'form.error.input.missing' })

  return (
    <Controller
      control={control}
      name="subject"
      render={({ field, fieldState }) => {
        const subjectOptions =
          includePrefilledOption &&
          field.value &&
          options.every((option) => option.value !== field.value)
            ? [
                {
                  'data-qa-id': 'direct-contact-form-prefilled-subject',
                  value: field.value,
                },
                ...options,
              ]
            : options

        return (
          <FormControl
            className="w-full"
            disabled={disabled}
            error={Boolean(fieldState.error)}
            required
            value={field.value}
          >
            <FormLabel htmlFor="subject">{label}</FormLabel>
            <Select
              data-qa-id="contact-form-select-subject"
              id="subject"
              listboxOpen={isListboxOpen}
              onChange={(_event, value) => {
                if (!value) {
                  return
                }

                field.onChange(value)
                onSubjectChange()
              }}
              onListboxOpenChange={setIsListboxOpen}
              slotProps={{
                popup: {
                  style: { display: isListboxOpen ? undefined : 'none' },
                },
                ...(selectRootAriaLabel && {
                  root: { 'aria-label': selectRootAriaLabel },
                }),
              }}
              value={field.value}
            >
              {subjectOptions.map((option) => (
                <Option key={option.value} {...option}>
                  {renderOptionValue
                    ? renderOptionValue(option.value)
                    : option.value}
                </Option>
              ))}
            </Select>
            {fieldState.error?.message ? (
              <FormHelperText data-qa-id="contact-form-subject-error-msg">
                {fieldState.error.message}
              </FormHelperText>
            ) : null}
          </FormControl>
        )
      }}
      rules={{ required: requiredMessage }}
    />
  )
}
